import api from "./api";

// ------------------------------------------------------------
// Tipos
// ------------------------------------------------------------

/**
 * Opciones de respuesta del operario.
 * A-D son las del plazo corriendo. E ("si fui") aparece solo cuando el plazo
 * ya vencio: el turno paso, asi que las opciones en futuro no aplican.
 */
export type OpcionRespuestaGestion = "A" | "B" | "C" | "D" | "E";

export interface GestionAusenciaActiva {
  gestion_id: number;
  estado: string;
  semaforo: string;
  fecha_turno: string;
  hora_entrada: string | null;
  hora_salida: string | null;
  punto_servicio: string;
  punto_servicio_id: number;
  operario: string;
  cedula: string;
}

export interface RespuestaCuerpo {
  opcion: OpcionRespuestaGestion;
  /** Solo para opcion A (voy a llegar). Valores validos: 10, 20, 30. */
  tiempo_estimado_min?: number;
  /** Solo para opcion B (no voy a asistir). Motivo elegido de la lista. */
  motivo_ausencia?: string;
  /** Solo para opcion B con motivo "Otro". Descripcion escrita por el operario. */
  motivo_texto?: string;
  /** Solo para opcion C (no puedo confirmar mi presencia). Validacion GPS automatica. */
  latitud?: number;
  /** Solo para opcion C (no puedo confirmar mi presencia). Validacion GPS automatica. */
  longitud?: number;
}

export interface RespuestaGestion {
  ok: boolean;
  estado_nuevo?: string;
  semaforo_nuevo?: string;
  /** Solicitud "Sancion a Definir por RRHH" creada al confirmar la falta del dia */
  solicitud_rrhh_id?: number | null;
  error?: string;
}

export interface PilotoStatus {
  en_piloto: boolean;
}

// ------------------------------------------------------------
// Calls a api-central
// ------------------------------------------------------------

/**
 * Obtiene la gestion de ausencia activa del operario autenticado.
 * Retorna null si no hay ninguna activa (estado 404).
 */
export const getGestionActiva = async (): Promise<GestionAusenciaActiva | null> => {
  try {
    const { data } = await api.get("gestion-ausencia/activa");
    // El endpoint retorna { gestion: {...} | null }
    return data.gestion ?? null;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Envia la respuesta del operario a una gestion de ausencia.
 * @param gestionId ID de la gestion (viene del deep link de la push)
 * @param cuerpo    Opcion elegida + campos opcionales segun la opcion
 */
export const responderGestion = async (
  gestionId: number,
  cuerpo: RespuestaCuerpo
): Promise<RespuestaGestion> => {
  const { data } = await api.post(`gestion-ausencia/${gestionId}/responder`, cuerpo);
  return data as RespuestaGestion;
};

/**
 * Indica si el usuario autenticado esta dentro del piloto del motor de ausencias.
 * Usado por la PWA para mostrar el banner de activar notificaciones solo a los
 * usuarios del fiscal piloto (y al fiscal mismo).
 * Si el backend falla, retorna en_piloto=false (degradacion silenciosa).
 */
export const getPilotoStatus = async (): Promise<PilotoStatus> => {
  try {
    const { data } = await api.get("gestion-ausencia/piloto-status");
    return { en_piloto: Boolean(data?.en_piloto) };
  } catch {
    return { en_piloto: false };
  }
};

// ------------------------------------------------------------
// Catalogo de motivos de ausencia (opcion B)
// ------------------------------------------------------------

/**
 * Lista de respaldo. Solo se usa si no se puede consultar el catalogo:
 * sin motivos el operario no puede terminar de avisar que no va a ir, y eso
 * es peor que mostrar una lista momentaneamente desactualizada.
 * La fuente real es EMSYS (Operarios/utils_gestion.py MOTIVOS_AUSENCIA).
 */
export const MOTIVOS_AUSENCIA_FALLBACK = [
  "Problemas de salud",
  "Reposo medico",
  "Permiso",
  "Emergencia familiar",
  "Problemas de transporte",
  "Otro",
];

/** Motivo que obliga a describir el caso a mano. */
export const MOTIVO_AUSENCIA_OTRO = "Otro";

export interface CatalogoMotivos {
  motivos: string[];
  motivo_otro: string;
}

/**
 * Trae el catalogo de motivos de ausencia.
 * Si el backend no responde devuelve la lista de respaldo, para que la
 * pantalla siempre pueda mostrar el selector.
 */
export const getMotivosAusencia = async (): Promise<CatalogoMotivos> => {
  try {
    const { data } = await api.get("gestion-ausencia/motivos");
    const motivos = Array.isArray(data?.motivos) ? data.motivos.map(String) : [];
    if (motivos.length) {
      return {
        motivos,
        motivo_otro: String(data?.motivo_otro || MOTIVO_AUSENCIA_OTRO),
      };
    }
  } catch {
    // degradacion silenciosa: se usa el respaldo
  }
  return {
    motivos: MOTIVOS_AUSENCIA_FALLBACK,
    motivo_otro: MOTIVO_AUSENCIA_OTRO,
  };
};

// ------------------------------------------------------------
// Bandeja del fiscalizador (modulo Mis Casos)
// ------------------------------------------------------------

export interface CasoFiscalizador {
  id: number;
  estado: string;
  semaforo: string;
  fecha_turno: string;
  hora_entrada: string | null;
  hora_salida: string | null;
  respuesta_operario: string | null;
  observacion_cc: string | null;
  observacion_fiscalizador: string | null;
  presencia_metodo: string | null;
  presencia_distancia_m: number | null;
  motivo_ausencia: string | null;
  /** El operario falto a todas sus asignaciones del dia (lo marca EMSYS) */
  falta_dia_completo: boolean;
  solicitud_rrhh_id: number | null;
  operario_id: number;
  cedula: string;
  nombre_operario: string;
  telefono: string | null;
  punto_servicio: string;
  fecha_creado: string | null;
  fecha_modificado: string | null;
  tiempo_en_estado_min: number | null;
  /** 1 si el caso fue derivado explicitamente a este fiscal por el CC */
  derivado_a_mi: number;
}

/**
 * falto_asignacion: el fiscal da fe de que falto a ESA asignacion (no va a RRHH).
 * falto_dia: da fe de que falto a todas las del dia; EMSYS confirma todos los
 * casos del dia y crea la solicitud "Sancion a Definir por RRHH".
 */
export type AccionFiscal =
  | "confirmar_presencia"
  | "corregir_asignacion"
  | "cerrar"
  | "falto_asignacion"
  | "falto_dia";

/**
 * Casos del fiscalizador autenticado (derivados a el o de sus puntos de
 * servicio). Por defecto trae solo los del dia que requieren su accion.
 * @param desde fecha YYYY-MM-DD inicio del rango (opcional, default hoy)
 * @param hasta fecha YYYY-MM-DD fin del rango (opcional, default = desde)
 * @param soloPendientes true = solo estados accionables; false = todos
 */
export const getCasosFiscalizador = async (
  desde?: string,
  hasta?: string,
  soloPendientes: boolean = true
): Promise<CasoFiscalizador[]> => {
  const params: Record<string, string> = {
    solo_pendientes: soloPendientes ? "1" : "0",
  };
  if (desde) params.desde = desde;
  if (hasta) params.hasta = hasta;
  const { data } = await api.get("gestion-ausencia/fiscalizador", { params });
  return (data?.gestiones ?? []) as CasoFiscalizador[];
};

/**
 * Ejecuta una accion del fiscalizador sobre un caso.
 * La observacion es obligatoria para "cerrar", "falto_asignacion" y "falto_dia".
 */
export const accionFiscal = async (
  gestionId: number,
  accion: AccionFiscal,
  observacion: string = ""
): Promise<RespuestaGestion> => {
  const { data } = await api.patch(`gestion-ausencia/${gestionId}/fiscal-accion`, {
    accion,
    observacion,
  });
  return data as RespuestaGestion;
};
